import React, { useEffect, useMemo, useState } from "react";
import { api, CAD } from "../lib/api";
import { Input } from "../components/ui/input";
import { Button } from "../components/ui/button";
import { Label } from "../components/ui/label";
import { Textarea } from "../components/ui/textarea";
import { toast } from "sonner";
import { Save, Trash2, Plus, DollarSign } from "lucide-react";

export default function ImmigrationFees() {
  const [types, setTypes] = useState([]);
  const [fees, setFees] = useState([]);
  const [selectedTypeId, setSelectedTypeId] = useState("");
  const [draft, setDraft] = useState({ base_fee: "", tax_rate: "0", notes: "" });
  const [saving, setSaving] = useState(false);

  const load = async () => {
    const [t, f] = await Promise.all([
      api.get("/immigration/app-types?include_inactive=false"),
      api.get("/immigration/fees"),
    ]);
    setTypes(t.data || []); setFees(f.data || []);
  };
  useEffect(() => { load(); }, []);

  const selectedType = useMemo(() => types.find(t => t.id === selectedTypeId), [types, selectedTypeId]);
  const existing = useMemo(() => fees.find(f => f.app_type_id === selectedTypeId), [fees, selectedTypeId]);
  useEffect(() => {
    if (existing) setDraft({ base_fee: String(existing.base_fee ?? ""), tax_rate: String(existing.tax_rate ?? "0"), notes: existing.notes || "" });
    else setDraft({ base_fee: "", tax_rate: "0", notes: "" });
  }, [existing, selectedTypeId]);

  const save = async () => {
    if (!selectedTypeId) return toast.error("Pick an application type first");
    const payload = {
      base_fee: parseFloat(draft.base_fee || "0") || 0,
      tax_rate: parseFloat(draft.tax_rate || "0") || 0,
      notes: draft.notes || "",
    };
    if (payload.base_fee < 0) return toast.error("Base fee cannot be negative");
    if (payload.tax_rate < 0 || payload.tax_rate > 1) return toast.error("Tax rate must be between 0 and 1 (0.13 = 13%)");
    setSaving(true);
    try {
      if (existing) await api.patch(`/immigration/fees/${existing.id}`, payload);
      else await api.post("/immigration/fees", { app_type_id: selectedTypeId, ...payload });
      toast.success("Fee saved");
      await load();
    } catch (e) { toast.error(e.response?.data?.detail || "Failed"); }
    finally { setSaving(false); }
  };

  const remove = async () => {
    if (!existing) return;
    if (!window.confirm("Delete this fee setup? Existing invoices are not affected.")) return;
    try { await api.delete(`/immigration/fees/${existing.id}`); toast.success("Deleted"); await load(); }
    catch (e) { toast.error(e.response?.data?.detail || "Failed"); }
  };

  const typesWithStatus = types.map(t => ({ ...t, has_fee: fees.some(f => f.app_type_id === t.id) }));

  return (
    <div className="max-w-5xl mx-auto space-y-6">
      <div>
        <h1 className="heading-font text-3xl font-bold text-slate-900">Fees Master Data</h1>
        <p className="text-sm text-slate-500 mt-1">
          Set the base professional fee + tax rate per application type. Each case will pre-populate its Fees tab from this master —
          officers can still override on a per-case basis and request a discount which routes to a manager for approval.
        </p>
      </div>

      <div className="grid grid-cols-1 md:grid-cols-[280px_1fr] gap-6">
        <div className="bg-white rounded-xl border border-slate-200 p-3 h-fit">
          <div className="text-xs uppercase text-slate-500 px-2 pb-2">Application Types</div>
          <div className="space-y-1">
            {typesWithStatus.map(t => (
              <button
                key={t.id}
                data-testid={`imm-fee-type-${t.code || t.id}`}
                onClick={() => setSelectedTypeId(t.id)}
                className={`w-full text-left px-3 py-2 rounded-lg text-sm flex items-center justify-between gap-2 ${
                  selectedTypeId === t.id ? "bg-orange-50 text-orange-800 border border-orange-200" : "hover:bg-slate-50 border border-transparent"
                }`}
              >
                <div className="flex items-center gap-2 min-w-0">
                  <span className="font-medium truncate">{t.name}</span>
                  <span className="mono text-[10px] text-slate-500">{t.code || "—"}</span>
                </div>
                {t.has_fee ? (
                  <span className="mono text-[11px] text-emerald-700">
                    {CAD(fees.find(f => f.app_type_id === t.id)?.base_fee || 0)}
                  </span>
                ) : (
                  <span className="text-[10px] text-slate-400">—</span>
                )}
              </button>
            ))}
          </div>
        </div>

        <div className="bg-white rounded-xl border border-slate-200 p-5 space-y-4">
          {!selectedTypeId ? (
            <div className="text-sm text-slate-500 py-10 text-center">Pick an application type on the left to configure its fee.</div>
          ) : (
            <>
              <div className="flex items-start justify-between gap-3 flex-wrap">
                <div>
                  <div className="text-xs uppercase text-slate-500">Editing</div>
                  <div className="heading-font text-xl font-semibold">{selectedType?.name}</div>
                  <div className="text-xs text-slate-500 mt-0.5">Code: {selectedType?.code || "—"} · {existing ? "Editing existing fee" : "New fee setup"}</div>
                </div>
                <div className="flex gap-2">
                  {existing && (
                    <Button variant="outline" onClick={remove} className="text-rose-600 border-rose-200 hover:bg-rose-50" data-testid="imm-fee-delete">
                      <Trash2 className="w-4 h-4 mr-1"/>Delete
                    </Button>
                  )}
                  <Button onClick={save} disabled={saving} className="bg-orange-500 hover:bg-orange-600 text-white" data-testid="imm-fee-save">
                    <Save className="w-4 h-4 mr-1"/>{saving ? "Saving…" : (existing ? "Save Changes" : "Create Fee")}
                  </Button>
                </div>
              </div>

              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div>
                  <Label className="text-xs text-slate-600">Base Professional Fee (CAD)</Label>
                  <div className="relative">
                    <DollarSign className="w-4 h-4 text-slate-400 absolute left-3 top-1/2 -translate-y-1/2"/>
                    <Input
                      data-testid="imm-fee-base"
                      type="number"
                      min="0" step="0.01"
                      value={draft.base_fee}
                      onChange={e => setDraft({ ...draft, base_fee: e.target.value })}
                      className="pl-8"
                      placeholder="2500.00"
                    />
                  </div>
                </div>
                <div>
                  <Label className="text-xs text-slate-600">Tax Rate (decimal, e.g. 0.13 for 13% HST)</Label>
                  <Input
                    data-testid="imm-fee-tax"
                    type="number"
                    min="0" max="1" step="0.001"
                    value={draft.tax_rate}
                    onChange={e => setDraft({ ...draft, tax_rate: e.target.value })}
                    placeholder="0.13"
                  />
                </div>
                <div className="md:col-span-2">
                  <Label className="text-xs text-slate-600">Internal Notes</Label>
                  <Textarea rows={3} value={draft.notes} onChange={e => setDraft({ ...draft, notes: e.target.value })} placeholder="e.g. includes DLI assessment but excludes GIC setup"/>
                </div>
              </div>
            </>
          )}
        </div>
      </div>
    </div>
  );
}
